import { CELL, type CityMap } from '../game/map';
import type { MomView } from '../game/MomTexts';
import type { RouteView } from '../game/Directions';
import { drawPhoneMap, renderMapBase } from '../ui/phoneMap';

type EndInfo = {
  title: string;
  lines: string[];
  hint: string;
  stats: [string, string][];
  win: boolean;
  /** Label for the 'next night' button, if there is one. */
  next?: string | null;
};

function el<T extends HTMLElement = HTMLElement>(selector: string): T {
  const e = document.querySelector<T>(selector);
  if (!e) throw new Error(`Missing element ${selector}`);
  return e;
}

export class Hud {
  private readonly monologue = el('#monologue');
  private readonly paceLabel = el('#pace-label');
  private readonly paceDots = Array.from(document.querySelectorAll<HTMLElement>('#pace-dots i'));
  private readonly pace = el('#pace');
  private readonly prompt = el('#prompt');
  private readonly promptText = el('#prompt-text');
  private readonly promptFill = el('#prompt-fill');
  private readonly phone = el('#phone');
  private readonly phoneCanvas = el<HTMLCanvasElement>('#phone-map');
  private readonly phoneClock = el('#phone-clock');
  private readonly phoneDist = el('#phone-dist');
  private readonly fadeEl = el('#fade');
  private readonly title = el('#title');
  private readonly end = el('#end');
  private readonly pause = el('#pause');
  private readonly hint = el('#hint');
  private readonly debug = el('#debug');
  private queue: { text: string; duration: number }[] = [];
  private lineTime = 0;
  private lineDuration = 0;
  private showingLine = false;
  private mapImage: HTMLCanvasElement | null = null;
  private mapHome: { x: number; z: number } | null = null;
  private chatKey = '';
  /** The Play button's label, and the loading state shown on it (see setLoading). */
  private beginLabel = 'Begin';
  private loadState: number | null = null;
  private loadShown = -1;
  /** Called when a quick-reply button on the on-screen card is pressed. */
  onMomReply: ((choice: number) => void) | null = null;

  say(text: string, duration = 3.8, urgent = false): void {
    if (urgent) {
      this.queue = [];
      this.showLine(text, duration);
      return;
    }
    if (!this.showingLine) this.showLine(text, duration);
    else if (this.queue.length < 3) this.queue.push({ text, duration });
  }

  clearLines(): void {
    this.queue = [];
    this.showingLine = false;
    this.monologue.classList.remove('visible');
  }

  private showLine(text: string, duration: number): void {
    this.monologue.textContent = text;
    this.monologue.classList.add('visible');
    this.showingLine = true;
    this.lineTime = 0;
    this.lineDuration = duration;
  }

  update(dt: number): void {
    if (!this.showingLine) return;
    this.lineTime += dt;
    if (this.lineTime > this.lineDuration) {
      this.monologue.classList.remove('visible');
      if (this.lineTime > this.lineDuration + 0.6) {
        this.showingLine = false;
        const next = this.queue.shift();
        if (next) this.showLine(next.text, next.duration);
      }
    }
  }

  setPace(label: string, gait: number): void {
    if (this.paceLabel.textContent !== label) this.paceLabel.textContent = label;
    this.paceDots.forEach((d, i) => d.classList.toggle('on', i < gait));
  }

  setHud(visible: boolean): void {
    this.pace.classList.toggle('hidden', !visible);
  }

  setHint(text: string | null): void {
    this.hint.classList.toggle('visible', !!text);
    if (text) this.hint.innerHTML = text;
  }

  setPrompt(text: string | null, progress = 0): void {
    this.prompt.classList.toggle('visible', !!text);
    if (text) {
      this.promptText.textContent = text;
      this.promptFill.style.transform = `scaleX(${Math.min(1, progress).toFixed(3)})`;
    }
  }

  setPhone(open: boolean): void {
    this.setPhoneApp(open ? 'map' : 'none', false);
  }

  /**
   * She is looking at her map: the world dims and blurs behind it. Without a
   * linked phone the map appears on an on-screen phone; with one, a cue points
   * the player at the phone in their hand.
   */
  setPhoneApp(app: 'none' | 'map' | 'chat', linked: boolean, call = false): void {
    const looking = app !== 'none';
    // A call brings her phone up on screen even when the player's own phone is linked.
    el('#game-canvas').classList.toggle('phone-focus', looking || call);
    this.phone.classList.toggle('open', (looking && !linked) || call);
    this.phone.classList.toggle('chat', app === 'chat' && !call);
    el('#phone-hint').classList.toggle('visible', looking && linked && !call);
    this.monologue.classList.toggle('beside-phone', (looking && !linked) || call);
  }

  /** The phone jolting in her hand (his face is on it). */
  phoneScare(on: boolean): void {
    this.phone.classList.toggle('scare', on);
  }

  drawPhone(map: CityMap, x: number, z: number, heading: number, homeMeters: number, clock: string, time: number, route?: RouteView | null): void {
    const ctx = this.phoneCanvas.getContext('2d');
    if (!ctx) return;
    // Zoomed in: about nine street blocks across, scrolling with her.
    const s = Math.round(this.phoneCanvas.width / 9);
    if (!this.mapImage) {
      this.mapImage = renderMapBase(map, s);
      const [home] = map.find('H');
      this.mapHome = { x: (home.c + 0.5) * CELL, z: (home.r + 0.5) * CELL };
    }
    drawPhoneMap(ctx, this.mapImage, s, x, z, heading, time, this.mapHome ?? undefined, route);
    this.phoneClock.textContent = clock;
    this.phoneDist.textContent = `Home · ${Math.max(0, Math.round(homeMeters / 10) * 10)} m`;
  }

  setPhoneLink(state: 'unavailable' | 'waiting' | 'connected', url?: string, qr?: string): void {
    for (const panel of Array.from(document.querySelectorAll<HTMLElement>('.phone-link'))) {
      panel.classList.toggle('hidden', state === 'unavailable');
      panel.classList.toggle('connected', state === 'connected');
      const img = panel.querySelector<HTMLImageElement>('img');
      if (img && qr && img.src !== qr) img.src = qr;
      const link = panel.querySelector<HTMLElement>('.phone-link-url');
      if (link && url) link.textContent = url;
    }
    el('#phone-badge').classList.toggle('visible', state === 'connected');
  }

  /** Render the messages app (on-screen phone). */
  setChat(view: MomView | null): void {
    const phone = this.phone;
    if (!view) {
      this.chatKey = '';
      return;
    }
    const thread = el('#chat-thread');
    const key = `${view.thread.length}|${view.momTyping}|${view.id}|${view.typing !== null}`;
    if (key !== this.chatKey) {
      this.chatKey = key;
      thread.replaceChildren(
        ...view.thread.slice(-7).map((m) => {
          const b = document.createElement('div');
          b.className = `bubble ${m.from}`;
          b.textContent = m.text;
          return b;
        }),
      );
      if (view.momTyping) {
        const t = document.createElement('div');
        t.className = 'bubble mom typing';
        t.append(document.createElement('i'), document.createElement('i'), document.createElement('i'));
        thread.append(t);
      }
      el('#chat-replies').replaceChildren(
        ...view.options.map((text, i) => {
          const b = document.createElement('button');
          b.type = 'button';
          const k = document.createElement('kbd');
          k.textContent = String(i + 1);
          b.append(k, document.createTextNode(text));
          b.addEventListener('pointerdown', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.onMomReply?.(i);
          });
          return b;
        }),
      );
    }
    const waiting = view.options.length > 0;
    phone.classList.toggle('urgent', waiting && view.timeLeft < 6);
    el('#chat-timer-fill').style.transform = `scaleX(${(waiting ? view.timeLeft / view.timeTotal : 0).toFixed(3)})`;
    // Her reply appears in the text field letter by letter while she types.
    const input = el('#chat-input-text');
    const typed = view.typing !== null && view.typingText ? view.typingText.slice(0, Math.ceil(view.typingText.length * view.typing)) : '';
    input.textContent = typed || 'iMessage';
    input.parentElement?.classList.toggle('active', !!typed);
    el('#chat-hint').textContent = waiting ? '1 / 2 reply · Tab put the phone away' : 'Tab: put the phone away';
  }

  /** The level changed: redraw the phone's map from the new streets. */
  resetPhoneMap(): void {
    this.mapImage = null;
    this.mapHome = null;
  }

  /**
   * The Play button waits while the chosen night is still loading: disabled,
   * showing the progress (null = ready).
   */
  setLoading(fraction: number | null): void {
    this.loadState = fraction;
    const shown = fraction === null ? 101 : Math.min(99, Math.floor(fraction * 100));
    if (shown === this.loadShown) return;
    this.loadShown = shown;
    const b = el<HTMLButtonElement>('#begin-button');
    b.disabled = fraction !== null;
    b.classList.toggle('loading', fraction !== null);
    b.textContent = fraction === null ? this.beginLabel : `Loading… ${shown}%`;
  }

  /** Title screen: the chosen night's story, the Begin button and the night chips. */
  setNights(
    levels: { night: number; title: string; subtitle: string; card: string[] }[],
    unlocked: number,
    current: number,
    onSelect: (i: number) => void,
  ): void {
    const lvl = levels[current];
    if (lvl) {
      el('#title-night-label').textContent = `Day ${lvl.night} · ${lvl.subtitle}`;
      // Night 1 shares the game's name; don't repeat it under the logo.
      const name = el('#title-night-name');
      name.textContent = lvl.title;
      name.classList.toggle('hidden', lvl.title === 'Walk Home');
      el('#title-story').replaceChildren(
        ...lvl.card.map((line, i) => {
          const p = document.createElement('p');
          p.textContent = line;
          if (i === lvl.card.length - 1) p.className = 'quiet';
          return p;
        }),
      );
    }
    this.beginLabel =
      current === 0 && unlocked === 1 ? 'Begin' : current === unlocked - 1 ? `Continue · Day ${lvl?.night}` : `Play day ${lvl?.night}`;
    this.loadShown = -1;
    this.setLoading(this.loadState);

    const nav = el('#night-select');
    nav.classList.toggle('hidden', levels.length < 2);
    el('#night-progress').textContent = `${Math.min(unlocked, levels.length)} of ${levels.length} reached`;
    nav.querySelector('.night-chips')?.replaceChildren(
      ...levels.map((l, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        const locked = i >= unlocked;
        b.className = `night${i === current ? ' current' : ''}${i < unlocked - 1 ? ' done' : ''}`;
        b.disabled = locked;
        b.textContent = locked ? '' : String(l.night);
        b.title = locked ? `Day ${l.night}: locked` : `Day ${l.night}: ${l.title} (${l.subtitle})`;
        b.setAttribute('aria-label', b.title);
        if (i === current) b.setAttribute('aria-current', 'true');
        b.addEventListener('click', () => onSelect(i));
        return b;
      }),
    );
  }

  showHowTo(show: boolean): void {
    el('#howto').classList.toggle('hidden', !show);
    if (show) el<HTMLButtonElement>('#howto-close').focus();
  }

  /** "Night 2 · Tuesday, rain" card as a night starts. */
  showCard(level: { night: number; title: string; subtitle: string; card: string[] }): void {
    const card = el('#night-card');
    el('#card-night').textContent = `Day ${level.night}`;
    el('#card-title').textContent = level.title;
    el('#card-sub').textContent = level.subtitle;
    el('#card-lines').replaceChildren(
      ...level.card.map((line) => {
        const p = document.createElement('p');
        p.textContent = line;
        return p;
      }),
    );
    card.classList.remove('show');
    void card.offsetWidth;
    card.classList.add('show');
  }

  fade(opacity: number, seconds: number): void {
    this.fadeEl.style.transitionDuration = `${seconds}s`;
    this.fadeEl.style.opacity = String(opacity);
  }

  showTitle(show: boolean): void {
    this.title.classList.toggle('hidden', !show);
  }

  showPause(show: boolean): void {
    this.pause.classList.toggle('hidden', !show);
    this.phone.classList.toggle('paused', show);
    this.monologue.classList.toggle('paused', show);
  }

  setBattery(percent: number, torchOn: boolean, torchWanted: boolean): void {
    const pct = Math.max(0, Math.ceil(percent));
    const hud = el('#battery-hud');
    el('#battery-pct').textContent = `${pct}%`;
    el('#battery-fill').style.width = `${Math.min(100, Math.max(0, percent) * (100 / 18)) * 0.9}%`;
    hud.classList.toggle('low', pct <= 5);
    hud.classList.toggle('critical', pct <= 2);
    const torch = el('#torch-state');
    torch.textContent = torchOn ? 'Flashlight on' : torchWanted ? 'Flashlight (phone in use)' : 'Flashlight off';
    torch.classList.toggle('on', torchOn);
    const phoneBattery = this.phone.querySelector<HTMLElement>('.battery');
    if (phoneBattery) {
      const label = `${pct}%`;
      if (phoneBattery.lastChild?.textContent !== label) phoneBattery.lastChild!.textContent = label;
    }
  }

  setRed(on: boolean): void {
    el('#red').classList.toggle('visible', on);
  }

  /** A text arriving on her (dead) phone, shown under the end title. */
  addEndText(from: string, text: string): void {
    const box = el('#end-texts');
    const t = document.createElement('div');
    t.className = 'text';
    const who = document.createElement('small');
    who.textContent = from;
    t.append(who, document.createTextNode(text));
    box.append(t);
    while (box.children.length > 4) box.firstElementChild?.remove();
  }

  showEnd(info: EndInfo | null): void {
    el('#end-texts').replaceChildren();
    if (!info) {
      this.end.classList.add('hidden');
      return;
    }
    el('#end-title').textContent = info.title;
    const lines = el('#end-lines');
    lines.replaceChildren(
      ...info.lines.map((l, i) => {
        const p = document.createElement('p');
        p.textContent = l;
        p.style.animationDelay = `${0.6 + i * 1.3}s`;
        return p;
      }),
    );
    const hint = el('#end-hint');
    hint.textContent = info.hint;
    hint.style.animationDelay = `${0.8 + info.lines.length * 1.3}s`;
    const stats = el('#end-stats');
    stats.replaceChildren(
      ...info.stats.flatMap(([k, v]) => {
        const dt = document.createElement('dt');
        dt.textContent = k;
        const dd = document.createElement('dd');
        dd.textContent = v;
        return [dt, dd];
      }),
    );
    stats.style.animationDelay = `${1.0 + info.lines.length * 1.3}s`;
    el('#retry-button').style.animationDelay = `${1.2 + info.lines.length * 1.3}s`;
    const next = el('#next-button');
    next.classList.toggle('hidden', !info.next);
    if (info.next) {
      next.textContent = info.next;
      next.style.animationDelay = `${1.1 + info.lines.length * 1.3}s`;
    }
    el('#retry-button').textContent = info.win ? 'Walk it again' : 'Try again';
    this.end.classList.toggle('win', info.win);
    this.end.classList.remove('hidden');
  }

  setDebug(text: string | null): void {
    this.debug.classList.toggle('visible', !!text);
    if (text) this.debug.textContent = text;
  }
}
